import { useMemo, useState, type ReactNode } from "react";
import type { MergedAccountView } from "@sales-ops-amplify/shared";
import {
  coalesceCompanyProfile,
  companyCommonFields,
  companyExtendedFields,
} from "@/lib/company-profile";

type FieldRow = { label: string; value: string };

export function CompanyFieldsTab({
  merged,
  crmEditor,
}: {
  merged: MergedAccountView;
  crmEditor?: ReactNode;
}) {
  const [q, setQ] = useState("");
  const profile = coalesceCompanyProfile(merged);

  const rows = useMemo((): FieldRow[] => {
    const common = companyCommonFields(profile);
    const extended = companyExtendedFields(profile);
    const fromHelpers = [...common, ...extended].map((f) => ({
      label: f.label,
      value: f.value,
    }));
    const user = merged.user;
    const extra: FieldRow[] = [
      { label: "Account ID", value: merged.accountId },
      { label: "Status", value: user?.status ?? "" },
      { label: "Priority", value: user?.priority ?? "" },
      {
        label: "Tags",
        value: Array.isArray(user?.tags) ? user.tags.join(", ") : "",
      },
      { label: "Notes", value: user?.notes ?? "" },
      {
        label: "External links",
        value:
          user?.externalLinks
            ?.map((l) => `${l.label}: ${l.url}`)
            .join("; ") ?? "",
      },
      { label: "ATS", value: user?.ats ?? "" },
      { label: "Board token", value: user?.boardToken ?? "" },
      {
        label: "Scan enabled",
        value:
          user?.scanEnabled == null ? "" : user.scanEnabled ? "Yes" : "No",
      },
    ];
    return [...fromHelpers, ...extra];
  }, [merged, profile]);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      r.label.toLowerCase().includes(needle) ||
      r.value.toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-4">
      {crmEditor}

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex min-w-0 flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">All fields</h3>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search fields…"
            className="w-full min-w-0 max-w-xs rounded-md border border-border bg-bg px-3 py-1.5 text-sm sm:w-56"
          />
        </div>
        <dl className="min-w-0 divide-y divide-border">
          {filtered.map((row) => (
            <div
              key={row.label}
              className="grid min-w-0 gap-1 py-2.5 sm:grid-cols-[180px_minmax(0,1fr)]"
            >
              <dt className="break-words text-xs font-medium text-muted-foreground">
                {row.label}
              </dt>
              <dd className="min-w-0 break-all text-sm text-foreground">
                {row.value || "—"}
              </dd>
            </div>
          ))}
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No fields match.
            </p>
          ) : null}
        </dl>
      </div>
    </div>
  );
}
